import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
	Search,
	Users,
	X,
	Plus,
	Calendar,
	Grid3X3,
	List,
	ListPlus,
	Check
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { listService } from "@/services/listService";
import { format } from "date-fns";
import { toast } from "sonner";

interface AddToListDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	candidateIds: string[];
	onSuccess?: () => void;
}

// Helper function to get current user's organization
const getCurrentOrganization = async () => {
	const { data: { user } } = await supabase.auth.getUser();
	if (!user) throw new Error('User not authenticated');

	const { data: userOrgs } = await supabase
		.from('user_organization')
		.select('organization_uuid')
		.eq('user_uuid', user.id)
		.single();

	if (!userOrgs) throw new Error('User not associated with any organization');
	return userOrgs.organization_uuid;
};

// Hook to get lists with membership info for candidates
const useListsForCandidates = (candidateIds: string[], enabled: boolean) => {
	return useQuery({
		queryKey: ['lists-for-candidates', candidateIds],
		queryFn: async () => {
			const organization = await getCurrentOrganization();
			if (!organization) {
				throw new Error('No organization found');
			}

			// Get all lists in the organization
			const { data: allLists, error: listsError } = await supabase
				.from('lists_with_candidate_count')
				.select('*')
				.eq('organization_uuid', organization)
				.order('created_at', { ascending: false });

			if (listsError) {
				throw listsError;
			}

			// Get list memberships for the selected candidates
			const { data: memberships, error: membershipsError } = await supabase
				.from('list_members')
				.select('list_id, candidate_id')
				.in('candidate_id', candidateIds);

			if (membershipsError) {
				throw membershipsError;
			}

			// Count how many selected candidates are in each list
			const listMembershipCount: Record<string, number> = {};
			memberships?.forEach(m => {
				listMembershipCount[m.list_id] = (listMembershipCount[m.list_id] || 0) + 1;
			});

			// Separate lists into:
			// - listsIn: lists where ALL selected candidates are already members
			// - listsPartial: lists where SOME but not all selected candidates are members
			// - listsNotIn: lists where NONE of the selected candidates are members
			const listsIn = allLists?.filter(list => listMembershipCount[list.id] === candidateIds.length) || [];
			const listsPartial = allLists?.filter(list => {
				const count = listMembershipCount[list.id] || 0;
				return count > 0 && count < candidateIds.length;
			}) || [];
			const listsNotIn = allLists?.filter(list => !listMembershipCount[list.id]) || [];

			return {
				listsIn,
				listsPartial,
				listsNotIn,
				allLists: allLists || [],
				membershipCount: listMembershipCount
			};
		},
		enabled: enabled && candidateIds.length > 0,
		staleTime: 30 * 1000, // 30 seconds
	});
};

export function AddToListDialog({
	open,
	onOpenChange,
	candidateIds,
	onSuccess
}: AddToListDialogProps) {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedLists, setSelectedLists] = useState<string[]>([]);
	const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
	const [isAdding, setIsAdding] = useState(false);

	const { data: listsData, isLoading, refetch } = useListsForCandidates(candidateIds, open);

	// Lists available to add to (not in + partial)
	const availableLists = useMemo(() => {
		if (!listsData) return [];
		return [...(listsData.listsNotIn || []), ...(listsData.listsPartial || [])];
	}, [listsData]);

	// Filter lists based on search term
	const filteredLists = useMemo(() => {
		const lowerSearchTerm = searchTerm.toLowerCase();
		return availableLists.filter(list =>
			list.name.toLowerCase().includes(lowerSearchTerm) ||
			(list.description && list.description.toLowerCase().includes(lowerSearchTerm))
		);
	}, [availableLists, searchTerm]);

	const handleListToggle = (listId: string) => {
		setSelectedLists(prev =>
			prev.includes(listId)
				? prev.filter(id => id !== listId)
				: [...prev, listId]
		);
	};

	const handleSelectAll = () => {
		if (selectedLists.length === filteredLists.length) {
			setSelectedLists([]);
		} else {
			setSelectedLists(filteredLists.map(l => l.id));
		}
	};

	const handleAdd = async () => {
		if (selectedLists.length === 0 || candidateIds.length === 0) return;

		setIsAdding(true);
		try {
			for (const listId of selectedLists) {
				await listService.addCandidatesToList({
					list_id: listId,
					candidate_ids: candidateIds
				});
			}

			toast.success(
				t("queries.addToList.success", "Added {{count}} candidates to {{listCount}} list(s)", {
					count: candidateIds.length,
					listCount: selectedLists.length
				})
			);
			setSelectedLists([]);
			setSearchTerm("");
			onOpenChange(false);
			refetch();
			queryClient.invalidateQueries({ queryKey: ['lists'] });
			onSuccess?.();
		} catch (error) {
			toast.error(t("queries.addToList.error", "Failed to add candidates to list"));
		} finally {
			setIsAdding(false);
		}
	};

	const handleClose = () => {
		setSelectedLists([]);
		setSearchTerm("");
		onOpenChange(false);
	};

	const getMembershipBadge = (listId: string) => {
		const count = listsData?.membershipCount[listId] || 0;
		if (count === 0) return null;
		if (count === candidateIds.length) return null; // All are members, shouldn't show in available lists
		return (
			<span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-sm">
				{t("queries.addToList.partialMembers", "{{count}}/{{total}} already in list", {
					count,
					total: candidateIds.length
				})}
			</span>
		);
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="max-w-[95vw] w-[1100px] h-[85vh] flex flex-col [&>button]:hidden">
				<DialogHeader className="pb-4">
					<div className="flex items-center justify-between">
						<div>
							<DialogTitle className="text-xl font-semibold flex items-center gap-2">
								<ListPlus className="h-5 w-5 text-blue-500" />
								{t("queries.addToList.title", "Add to List")}
							</DialogTitle>
							<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
								{t("queries.addToList.description", "Select lists to add {{count}} candidates to", {
									count: candidateIds.length
								})}
							</p>
						</div>
						<DialogClose asChild>
							<Button variant="ghost" size="icon" className="h-10 w-10">
								<X className="h-6 w-6" />
							</Button>
						</DialogClose>
					</div>
				</DialogHeader>

				<div className="flex-1 flex flex-col space-y-6 min-h-0">
					{/* Lists candidates are already in */}
					{listsData?.listsIn && listsData.listsIn.length > 0 && (
						<div className="space-y-4">
							<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
								<Check className="h-5 w-5 text-green-600" />
								{t("queries.addToList.alreadyIn", "All {{count}} candidates are in {{listCount}} lists", {
									count: candidateIds.length,
									listCount: listsData.listsIn.length
								})}
							</h3>

							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
								{listsData.listsIn.map((list) => (
									<div
										key={list.id}
										className="bg-white dark:bg-gray-800 border border-green-200 dark:border-green-800 rounded-sm p-3"
									>
										<div className="flex items-center space-x-3">
											<Avatar className="h-8 w-8">
												<AvatarFallback className="bg-green-100 text-green-600 text-xs">
													{list.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
												</AvatarFallback>
											</Avatar>
											<div className="flex-1 min-w-0">
												<h4 className="font-medium text-gray-900 dark:text-gray-100 truncate text-sm">{list.name}</h4>
												<div className="flex items-center gap-2">
													<Users className="h-3 w-3 text-gray-500" />
													<span className="text-xs text-gray-600 dark:text-gray-300">{list.candidate_count} candidates</span>
												</div>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					{/* Add to more lists section */}
					<div className="flex-1 flex flex-col space-y-4 min-h-0">
						<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
							<Plus className="h-5 w-5 text-blue-500" />
							{t("queries.addToList.addToMore", "Add to Lists")}
						</h3>

						{/* Search Bar */}
						<div className="relative">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
							<Input
								placeholder={t("queries.addToList.searchPlaceholder", "Search lists by name or description...")}
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="pl-10 pr-10"
							/>
							{searchTerm && (
								<Button
									type="button"
									variant="ghost"
									size="icon"
									className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
									onClick={() => setSearchTerm('')}
								>
									<X className="h-4 w-4" />
								</Button>
							)}
						</div>

						{/* View Toggle & Results Count */}
						<div className="flex items-center justify-between">
							{searchTerm ? (
								<p className="text-sm text-gray-600 dark:text-gray-300">
									{t("queries.addToList.listsFound", "{{count}} lists found", { count: filteredLists.length })}
								</p>
							) : (
								<p className="text-sm text-gray-600 dark:text-gray-300">
									{t("queries.addToList.totalLists", "{{count}} lists available", { count: filteredLists.length })}
								</p>
							)}
							<div className="flex items-center gap-2">
								<span className="text-sm text-gray-500 dark:text-gray-400">{t("common.view", "View")}:</span>
								<ToggleGroup
									type="single"
									value={viewMode}
									onValueChange={(value) => value && setViewMode(value as 'grid' | 'list')}
									className="h-8 bg-gray-50 dark:bg-gray-800/50 rounded-sm p-1"
								>
									<ToggleGroupItem
										value="grid"
										className="h-6 w-6 p-0 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-500"
									>
										<Grid3X3 className="h-4 w-4" />
									</ToggleGroupItem>
									<ToggleGroupItem
										value="list"
										className="h-6 w-6 p-0 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-500"
									>
										<List className="h-4 w-4" />
									</ToggleGroupItem>
								</ToggleGroup>
							</div>
						</div>

						{/* Lists */}
						<div className="flex-1 min-h-0 overflow-hidden">
							{isLoading ? (
								<div className="flex items-center justify-center h-32">
									<div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
								</div>
							) : filteredLists.length === 0 ? (
								<div className="flex items-center justify-center h-32">
									<div className="text-center">
										<Users className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
										<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
											{searchTerm
												? t("queries.addToList.noListsFound", "No lists found")
												: t("queries.addToList.noMoreLists", "No more lists available")}
										</h3>
										<p className="text-gray-600 dark:text-gray-300">
											{searchTerm
												? t("queries.addToList.tryAdjustingSearch", "Try adjusting your search terms")
												: t("queries.addToList.allInLists", "All candidates are already in all available lists")}
										</p>
									</div>
								</div>
							) : (
								<div className="h-full overflow-y-auto pr-2">
									{viewMode === 'grid' ? (
										<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
											{filteredLists.map((list) => (
												<div
													key={list.id}
													className={`relative bg-white dark:bg-gray-800 border rounded-sm cursor-pointer transition-all duration-200 hover:shadow-md ${
														selectedLists.includes(list.id)
															? 'ring-2 ring-inset ring-blue-500 border-blue-500 p-4'
															: 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 p-4'
													}`}
													onClick={() => handleListToggle(list.id)}
												>
													{/* Selection Checkbox */}
													<div className="absolute top-3 right-3">
														<Checkbox
															checked={selectedLists.includes(list.id)}
															onCheckedChange={() => handleListToggle(list.id)}
															className="h-4 w-4"
														/>
													</div>

													{/* List Info */}
													<div className="flex items-start space-x-3 mb-3">
														<Avatar className="h-10 w-10">
															<AvatarFallback className="bg-blue-100 text-blue-500">
																{list.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
															</AvatarFallback>
														</Avatar>
														<div className="flex-1 min-w-0 pr-6">
															<h4 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{list.name}</h4>
															<div className="flex items-center gap-2 mt-1">
																<Users className="h-3 w-3 text-gray-500" />
																<span className="text-sm text-gray-600 dark:text-gray-300">{list.candidate_count} candidates</span>
															</div>
														</div>
													</div>

													{/* Partial membership badge */}
													{getMembershipBadge(list.id)}

													{/* Description */}
													{list.description && (
														<p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2 mt-2">{list.description}</p>
													)}

													{/* Created Date */}
													<div className="flex items-center gap-2 mt-2">
														<Calendar className="h-3 w-3 text-gray-400" />
														<span className="text-xs text-gray-500">
															Created {format(new Date(list.created_at), 'MMM d, yyyy')}
														</span>
													</div>
												</div>
											))}
										</div>
									) : (
										<div className="space-y-2">
											{filteredLists.map((list) => (
												<div
													key={list.id}
													className={`flex items-center space-x-4 bg-white dark:bg-gray-800 border rounded-sm cursor-pointer transition-all duration-200 hover:shadow-sm ${
														selectedLists.includes(list.id)
															? 'ring-2 ring-inset ring-blue-500 border-blue-500 p-4'
															: 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 p-4'
													}`}
													onClick={() => handleListToggle(list.id)}
												>
													<Checkbox
														checked={selectedLists.includes(list.id)}
														onCheckedChange={() => handleListToggle(list.id)}
														className="h-4 w-4"
													/>

													<Avatar className="h-8 w-8">
														<AvatarFallback className="bg-blue-100 text-blue-500 text-xs">
															{list.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
														</AvatarFallback>
													</Avatar>

													<div className="flex-1 min-w-0">
														<div className="flex items-center gap-2">
															<h4 className="font-semibold text-gray-900 dark:text-gray-100">{list.name}</h4>
															{getMembershipBadge(list.id)}
														</div>
														{list.description && (
															<p className="text-sm text-gray-600 dark:text-gray-300 truncate">{list.description}</p>
														)}
													</div>

													<div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-300">
														<div className="flex items-center">
															<Users className="h-3 w-3 mr-1" />
															<span>{list.candidate_count} candidates</span>
														</div>
													</div>

													<div className="flex items-center gap-2">
														<Calendar className="h-3 w-3 text-gray-400" />
														<span className="text-xs text-gray-500 dark:text-gray-400">
															{format(new Date(list.created_at), 'MMM d, yyyy')}
														</span>
													</div>
												</div>
											))}
										</div>
									)}
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Footer */}
				<div className="border-t bg-gray-50 dark:bg-gray-800/50 -mx-6 -mb-6 px-6 py-4 flex items-center justify-between mt-4">
					<div className="flex items-center space-x-4">
						<div className="flex items-center space-x-2">
							<Checkbox
								checked={filteredLists.length > 0 && selectedLists.length === filteredLists.length}
								onCheckedChange={handleSelectAll}
								className="h-4 w-4"
							/>
							<span className="text-sm text-gray-600 dark:text-gray-300">
								{selectedLists.length === filteredLists.length
									? t("common.deselectAll", "Deselect all")
									: t("common.selectAll", "Select all")
								}
							</span>
						</div>
						{selectedLists.length > 0 && (
							<span className="text-sm text-gray-600">
								{t("queries.addToList.selectedCount", "{{count}} selected", { count: selectedLists.length })}
							</span>
						)}
					</div>

					<div className="flex items-center space-x-2">
						<Button variant="outline" onClick={handleClose}>
							{t("common.cancel", "Cancel")}
						</Button>
						<Button
							onClick={handleAdd}
							disabled={selectedLists.length === 0 || isAdding}
							className="bg-blue-500 hover:bg-blue-500"
						>
							{isAdding ? (
								<div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
							) : (
								<Plus className="h-4 w-4 mr-2" />
							)}
							{t("queries.addToList.addButton", "Add to {{count}} list(s)", { count: selectedLists.length })}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
